import React, { useEffect, useMemo } from "react";
import { Progress, Skeleton, useToast } from "randmarcomps";
import { useGetV4WarehousesQuery, Warehouse as ApiWarehouse } from "randmar-api-client";
import WarehouseInfoCard from "../componentsCore/cards/WarehouseInfoCard";
import { Warehouse as LegacyWarehouse } from "../stores/warehouses/Warehouse";
import withRoot from "../withRoot";

const WarehouseCardSkeleton = () => (
  <div className="p-4 border rounded-lg space-y-3">
    <Skeleton className="h-6 w-1/2" />
    <Skeleton className="h-4 w-3/4" />
    <Skeleton className="h-4 w-full" />
  </div>
);

const WarehouseInformation: React.FC = () => {
  const { data: apiWarehouses, isLoading, isError } = useGetV4WarehousesQuery();
  const { toast } = useToast();

  useEffect(() => {
    if (isError) {
      toast({
        variant: "destructive",
        title: "Error fetching warehouses",
        description: "There was an issue retrieving warehouse information. Please try again later.",
      });
    }
  }, [isError, toast]);

  const warehouses = useMemo(() => {
    return (apiWarehouses ?? []).map((w: ApiWarehouse) => {
      const legacy = new LegacyWarehouse();
      Object.assign(legacy, {
        address1: w.Address1,
        address2: w.Address2,
        ApplicationId: w.ApplicationId,
        city: w.City,
        contactEmail: w.ContactEmail,
        contactName: w.ContactName,
        contactPhone: w.ContactPhone,
        country: w.Country,
        integrationKeyName: w.IntegrationKeyName,
        name: w.Name,
        warehouseId: w.WarehouseId,
        province: w.Province,
        postalCode: w.PostalCode,
      });
      return legacy;
    });
  }, [apiWarehouses]);

  if (isLoading) {
    return (
      <main>
        <h1 className="text-2xl font-bold mb-4">Warehouse Information</h1>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <WarehouseCardSkeleton />
          <WarehouseCardSkeleton />
        </div>
      </main>
    );
  }

  if (warehouses.length === 0) {
    return (
      <main>
        <h1 className="text-2xl font-bold mb-4">Warehouse Information</h1>
        <div className="text-center border-2 border-dashed rounded-lg p-12 bg-white">
          <p className="text-gray-500">No warehouse information available.</p>
        </div>
      </main>
    );
  }

  return (
    <main>
      <Progress value={100} className="mb-5" />
      <h1 className="text-2xl font-bold mb-4">Warehouse Information</h1>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {warehouses.map((warehouse: LegacyWarehouse) => (
          <WarehouseInfoCard key={(warehouse as any).integrationKeyName} warehouse={warehouse} />
        ))}
      </div>
    </main>
  );
};

export default withRoot(WarehouseInformation);
